import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

export interface NoteAttachment {
  id: string
  filename: string
  mimeType: string
  sizeBytes: number
  url: string
}

export interface ChatNote {
  id: string
  chatId: string
  channel: string
  authorAgentId: string | null
  authorName: string | null
  body: string
  bodyHtml: string | null
  isPrivate: boolean
  attachments: NoteAttachment[]
  createdAt: string
  updatedAt: string
}

interface NotesResponse {
  notes: ChatNote[]
}

interface CreateNotePayload {
  body: string
  bodyHtml?: string
  isPrivate?: boolean
  attachments?: string[]
}

interface CreateNoteResponse {
  note: ChatNote
}

interface DeleteNoteResponse {
  deleted: boolean
}

interface UpdateNoteResponse {
  note: ChatNote
}

export interface UseChatNotesResult {
  notes: ChatNote[]
  loading: boolean
  error: string | null
  creating: boolean
  updating: boolean
  refetch: () => Promise<void>
  createNote: (payload: CreateNotePayload) => Promise<ChatNote | null>
  updateNote: (noteId: string, payload: CreateNotePayload) => Promise<ChatNote | null>
  deleteNote: (noteId: string) => Promise<boolean>
}

export function useChatNotes(chatId: string | null, channel: string | null): UseChatNotesResult {
  const [notes, setNotes] = useState<ChatNote[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)

  const baseUrl = chatId && channel ? `/agent/chats/${chatId}/notes` : null
  const query = channel ? `?channel=${encodeURIComponent(channel)}` : ''
  const listUrl = baseUrl ? `${baseUrl}${query}` : null

  const refetch = useCallback(async () => {
    if (!listUrl) {
      setNotes([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<NotesResponse>(listUrl)
      setNotes(res.notes)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notes')
      setNotes([])
    } finally {
      setLoading(false)
    }
  }, [listUrl])

  useEffect(() => {
    void refetch()
  }, [refetch])

  const createNote = useCallback(
    async (payload: CreateNotePayload): Promise<ChatNote | null> => {
      if (!listUrl) return null
      setCreating(true)
      try {
        const res = await api.post<CreateNoteResponse>(listUrl, payload)
        setNotes((prev) => [res.note, ...prev])
        return res.note
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create note')
        return null
      } finally {
        setCreating(false)
      }
    },
    [listUrl],
  )

  const updateNote = useCallback(
    async (noteId: string, payload: CreateNotePayload): Promise<ChatNote | null> => {
      if (!baseUrl || !channel) return null
      setUpdating(true)
      try {
        const res = await api.put<UpdateNoteResponse>(`${baseUrl}/${noteId}${query}`, payload)
        setNotes((prev) => prev.map((n) => (n.id === noteId ? res.note : n)))
        return res.note
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update note')
        return null
      } finally {
        setUpdating(false)
      }
    },
    [baseUrl, channel, query],
  )

  const deleteNote = useCallback(
    async (noteId: string): Promise<boolean> => {
      if (!baseUrl || !channel) return false
      try {
        const res = await api.delete<DeleteNoteResponse>(`${baseUrl}/${noteId}${query}`)
        if (res.deleted) {
          setNotes((prev) => prev.filter((n) => n.id !== noteId))
        }
        return res.deleted
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete note')
        return false
      }
    },
    [baseUrl, channel, query],
  )

  return { notes, loading, error, creating, updating, refetch, createNote, updateNote, deleteNote }
}
